import {
  SET_SELECTED_SAMPLE,
  TOGGLE_SAMPLE_INCLUDED,
  RESET_SAMPLE_SELECTION,
  SET_SAMPLE_NOTE,
  ADD_NEW_SAMPLE,
  RECEIVE_SET_STUDY,
  RESET_REDUX,
  SET_SAMPLE_DATA,
  SET_SWS_PLOT,
  SET_VELOCITY_SLICE_PLOT,
  SET_BMODE_PLOT,
  SET_SWS_VALUE,
  SET_Z_VALUE,
  SET_DZ,
  SET_V_MAX,
  SET_SAMPLE_TRIANGLE_POSITION,
  SET_PICTURE_IMAGE,
  RECEIVE_PROTOCOL,
  REMOVE_ERRORED_SAMPLE,
  REMOVE_ACQ_SAMPLE,
  SET_SAMPLE_ALERT,
  SET_BMODE_THICKNESS,
  SET_ROI_PERCENTAGE_SLICE,
  CANCEL_PHOTO,
  SET_SAMPLE_VALUE_DISPLAY,
  SET_SAMPLE_PROCESSING,
  SET_SAMPLE_SELECTABLE,
  SET_SAMPLE_DATA_AVAILABLE,
  SET_SAMPLE_DATA_SAVED,
  SET_SWS_RANGE,
  SET_B_RANGE,
  TOGGLE_BMODE_SET_TO_AUTO_DETECTED_THICKNESS,
  SET_SAMPLE_LOCATION_ID,
} from "../actions/index";
import produce from "immer";

const initialState = {
  selectedSampleId: null,
  trianglePositionY: -20,
  allIds: [],
  byId: {},
  idByType: {},
};

export default (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case RECEIVE_PROTOCOL: {
        // Create idByType variable based on the acq types in the protocol
        let typeDict = {};
        Object.keys(action.setProtocol.acq_types).forEach((acqType) => {
          typeDict[acqType] = [];
        });
        draft.idByType = typeDict;
        break;
      }

      case RECEIVE_SET_STUDY: {
        // Populate redux with the sample data from the uploaded study
        draft.byId = action.samples.byId;

        draft.allIds = action.samples.allIds;
        draft.selectedSampleId = null;
        draft.idByType = action.samples.idByType;
        break;
      }

      case CANCEL_PHOTO: {
        // remove id from byId and allIds, reset sample selection
        const { [draft.selectedSampleId]: value, ...withoutId } = draft.byId;
        draft.byId = withoutId;
        if (draft.allIds.length !== 0) {
          draft.allIds = draft.allIds.filter((id) => id !== action.sampleId);
        }
        if (draft.idByType.PICTURE) {
          draft.idByType.PICTURE = draft.idByType.PICTURE.filter(
            (id) => id !== action.sampleId
          );
        }

        draft.selectedSampleId = null;
        break;
      }

      case SET_SELECTED_SAMPLE:
        if (
          action.sampleId !== "SAMPLE_COLUMN" &&
          draft.byId[action.sampleId].selectable
        ) {
          draft.selectedSampleId = action.sampleId;
        } else if (action.sampleId === "SAMPLE_COLUMN") {
          draft.selectedSampleId = action.sampleId;
        }
        break;

      case RESET_SAMPLE_SELECTION: {
        draft.selectedSampleId = null;
        break;
      }

      case TOGGLE_SAMPLE_INCLUDED: {
        draft.byId[action.sampleId].includeInStudy = !draft.byId[
          action.sampleId
        ].includeInStudy;
        break;
      }

      case SET_SAMPLE_NOTE: {
        draft.byId[action.sampleId].notes = action.text;
        break;
      }

      case ADD_NEW_SAMPLE: {
        // Create a new sample entry with default values
        if (
          action.acqType === "PICTURE" &&
          !Object.keys(draft.idByType).includes(action.acqType)
        ) {
          draft.idByType[action.acqType] = [];
        }
        draft.idByType[action.acqType].push(action.id);
        draft.allIds.push(action.id);
        draft.byId[action.id] = {
          id: action.id,
          type: action.acqType,
          processing: true, //circular progress or check icon for sample
          valueDisplay: false, // display value or nothing
          selectable: false, //allow clicking on/selecting sample
          dataAvailable: false, //allow user to change ROI region / play with results graph
          dataSaved: false, //allow user save study file
          locationId: action.locationId, //location the sample belongs to
          alert: { state: false, type: [], message: null }, // tracks processing alerts - types include "SWS"=> for null sws, "SKIN_THICKNESS" => could not determine skin thickness, "SURFACE_DEPTH" => CSI samples
          error: { state: false, type: null }, // tracks error state -  during either type "ACQ" or type "PROCESSING"
          includeInStudy: true,
          notes: null,
        };
        break;
      }

      // Add image to sample
      case SET_PICTURE_IMAGE: {
        draft.byId[action.id].picture = action.data;
        break;
      }

      case SET_SAMPLE_DATA: {
        // Add sample data
        switch (action.sampleType) {
          case "BMODE":
            draft.byId[action.id].type = action.sampleType;
            draft.byId[action.id].image = action.response.image_data; // base64 data image
            draft.byId[action.id].thickness = action.response.thickness; // skin thickness in mm
            draft.byId[action.id].xAxis = {
              range: action.response.x_axis.range,
              unit: action.response.x_axis.unit,
              label: action.response.x_axis.label,
            };
            draft.byId[action.id].yAxis = {
              range: action.response.y_axis.range,
              unit: action.response.y_axis.unit,
              label: action.response.y_axis.label,
            };
            draft.byId[action.id].colorBar = {
              range: action.response.color_bar.range,
              unit: action.response.color_bar.unit,
              label: action.response.color_bar.label,
            };
            draft.byId[action.id].autoDetectedTopSurface =
              action.response.top_avg; // in mm
            draft.byId[action.id].autoDetectedBottomSurface =
              action.response.bottom_avg; // in mm
            draft.byId[action.id].topSurface = action.response.top_avg; // in mm
            draft.byId[action.id].bottomSurface = action.response.bottom_avg; // in mm
            draft.byId[action.id].zValue = action.response.z_val; // in mm
            draft.byId[action.id].calib = action.response.calib; // in mm
            draft.byId[action.id].setToAutoDetectedThickness = true;
            draft.byId[action.id].valueDisplay = true;
            draft.byId[action.id].selectable = true;
            draft.byId[action.id].processing = false;
            break;
          case "MMODE":
          case "STL":
            draft.byId[action.id].type = action.sampleType;
            draft.byId[action.id].roiDz = action.response.dz * 1e3; // in mm
            draft.byId[action.id].percentSliceWidth =
              action.response.percentSliceWidth;
            draft.byId[action.id].dz =
              (action.response.dz * 1e3 * 100) /
              action.response.percentSliceWidth; // in mm
            draft.byId[action.id].swsPlot = {
              image: action.response.sws_plot.image,
              xAxis: {
                range: action.response.sws_plot.x_axis.range,
                unit: action.response.sws_plot.x_axis.unit,
                label: action.response.sws_plot.x_axis.label,
              },
              yAxis: {
                range: action.response.sws_plot.y_axis.range,
                unit: action.response.sws_plot.y_axis.unit,
                label: action.response.sws_plot.y_axis.label,
              },
              colorBar: {
                xAxis: {
                  range: action.response.sws_plot.color_bar.x_axis.range,
                  unit: action.response.sws_plot.color_bar.x_axis.unit,
                  label: action.response.sws_plot.color_bar.x_axis.label,
                },
                yAxis: {
                  range: action.response.sws_plot.color_bar.y_axis.range,
                  unit: action.response.sws_plot.color_bar.y_axis.unit,
                  label: action.response.sws_plot.color_bar.y_axis.label,
                },
              },
            };

            //Multi frame data
            draft.byId[action.id].velocitySlicePlot = {
              image: action.response.velocity_slice_plot.image,
              peakData: [...action.response.velocity_slice_plot.peak_data],
              xAxis: {
                range: action.response.velocity_slice_plot.x_axis.range,
                unit: action.response.velocity_slice_plot.x_axis.unit,
                label: action.response.velocity_slice_plot.x_axis.label,
              },
              yAxis: {
                range: action.response.velocity_slice_plot.y_axis.range,
                unit: action.response.velocity_slice_plot.y_axis.unit,
                label: action.response.velocity_slice_plot.y_axis.label,
              },
              colorBar: {
                range: action.response.velocity_slice_plot.color_bar.range,
                unit: action.response.velocity_slice_plot.color_bar.unit,
                label: action.response.velocity_slice_plot.color_bar.label,
              },
            };
            draft.byId[action.id].zValue = action.response.z_val * 1e3;

            draft.byId[action.id].zList = {
              z0: action.response.z_list.z0,
              nz: action.response.z_list.nz,
              inc: action.response.z_list.inc,
            };
            draft.byId[action.id].shearWaveSpeed = action.response.sws_value; // m/s
            draft.byId[action.id].calib = action.response.calib; // in mm
            draft.byId[action.id].valueDisplay = true;
            draft.byId[action.id].selectable = true;
            draft.byId[action.id].processing = false;
            break;
          default:
            // SINGLE
            draft.byId[action.id].type = action.sampleType;
            draft.byId[action.id].roiDz = action.response.dz * 1e3; // in mm
            draft.byId[action.id].percentSliceWidth =
              action.response.percentSliceWidth;
            draft.byId[action.id].dz =
              (action.response.dz * 1e3 * 100) /
              action.response.percentSliceWidth; // in mm
            draft.byId[action.id].swsPlot = {
              image: action.response.sws_plot.image,
              xAxis: {
                range: action.response.sws_plot.x_axis.range,
                unit: action.response.sws_plot.x_axis.unit,
                label: action.response.sws_plot.x_axis.label,
              },
              yAxis: {
                range: action.response.sws_plot.y_axis.range,
                unit: action.response.sws_plot.y_axis.unit,
                label: action.response.sws_plot.y_axis.label,
              },
              colorBar: {
                xAxis: {
                  range: action.response.sws_plot.color_bar.x_axis.range,
                  unit: action.response.sws_plot.color_bar.x_axis.unit,
                  label: action.response.sws_plot.color_bar.x_axis.label,
                },
                yAxis: {
                  range: action.response.sws_plot.color_bar.y_axis.range,
                  unit: action.response.sws_plot.color_bar.y_axis.unit,
                  label: action.response.sws_plot.color_bar.y_axis.label,
                },
              },
            };
            draft.byId[action.id].zValue = action.response.z_val * 1e3; // in mm
            draft.byId[action.id].zList = {
              z0: action.response.z_list.z0,
              nz: action.response.z_list.nz,
              inc: action.response.z_list.inc,
            };
            draft.byId[action.id].shearWaveSpeed = action.response.sws_value; // m/s
            draft.byId[action.id].calib = action.response.calib; // in mm
            draft.byId[action.id].valueDisplay = true;
            draft.byId[action.id].selectable = true;
            draft.byId[action.id].processing = false;
            break;
        }
        break;
      }

      case SET_SAMPLE_DATA_AVAILABLE:
        draft.byId[action.id].dataAvailable = action.status;
        break;

      case SET_SAMPLE_DATA_SAVED:
        draft.byId[action.id].dataSaved = action.status;
        break;

      case SET_SWS_VALUE: {
        draft.byId[action.id].shearWaveSpeed = action.sws;
        break;
      }

      case SET_SAMPLE_ALERT: {
        if (action.alert.status) {
          // Add type to alert type aray
          draft.byId[action.id].alert.state = action.alert.status;
          draft.byId[action.id].alert.type.push(action.alert.type);
          draft.byId[action.id].alert.message = action.alert.message;
        } else if (!action.alert.status) {
          // Remove alert type from array
          draft.byId[action.id].alert.type.splice(
            draft.byId[action.id].alert.type.indexOf(action.alert.type),
            1
          );
          draft.byId[action.id].alert.state =
            draft.byId[action.id].alert.type.length !== 0;
          draft.byId[action.id].alert.message = null;
        }
        break;
      }

      case SET_SAMPLE_PROCESSING: {
        draft.byId[action.id].processing = action.status;
        break;
      }

      case SET_SAMPLE_VALUE_DISPLAY: {
        draft.byId[action.id].valueDisplay = action.status;
        break;
      }

      case SET_SAMPLE_SELECTABLE: {
        draft.byId[action.id].selectable = action.status;
        break;
      }

      case SET_SWS_PLOT: {
        draft.byId[action.id].swsPlot.image = action.response.image;
        draft.byId[action.id].swsPlot.colorBar = {
          xAxis: {
            range: action.response.color_bar.x_axis.range,
            unit: action.response.color_bar.x_axis.unit,
            label: action.response.color_bar.x_axis.label,
          },
          yAxis: {
            range: action.response.color_bar.y_axis.range,
            unit: action.response.color_bar.y_axis.unit,
            label: action.response.color_bar.y_axis.label,
          },
        };

        break;
      }

      case SET_VELOCITY_SLICE_PLOT: {
        draft.byId[action.id].velocitySlicePlot.image = action.response.image;
        draft.byId[action.id].velocitySlicePlot.peakData = [
          ...action.response.peak_data,
        ];
        draft.byId[action.id].velocitySlicePlot.colorBar =
          action.response.color_bar;
        draft.byId[action.id].velocitySlicePlot.xAxis = action.response.x_axis;
        draft.byId[action.id].velocitySlicePlot.yAxis = action.response.y_axis;
        break;
      }

      case SET_BMODE_PLOT: {
        draft.byId[action.id].image = action.response.image_data;
        draft.byId[action.id].autoDetectedTopSurface = action.response.top_avg; // in mm
        draft.byId[action.id].autoDetectedBottomSurface =
          action.response.bottom_avg; // in mm
        draft.byId[action.id].colorBar.range = action.bmodeRange;
        break;
      }

      case SET_Z_VALUE: {
        draft.byId[action.id].zValue = action.zValue;
        break;
      }

      case SET_SWS_RANGE: {
        draft.byId[action.id].swsPlot.colorBar.yAxis.range = action.swsRange;
        break;
      }
      case SET_B_RANGE: {
        draft.byId[action.id].swsPlot.colorBar.xAxis.range = action.bRange;
        break;
      }
      case SET_V_MAX: {
        draft.byId[action.id].velocitySlicePlot.colorBar.range[0] =
          -1 * action.vMax;
        draft.byId[action.id].velocitySlicePlot.colorBar.range[1] = action.vMax;
        break;
      }

      case SET_DZ: {
        draft.byId[action.id].dz = action.dz; // in mm
        draft.byId[action.id].roiDz =
          (action.dz * draft.byId[action.id].percentSliceWidth) / 100; // in mm
        break;
      }

      case SET_ROI_PERCENTAGE_SLICE: {
        draft.byId[action.id].percentSliceWidth = action.percentSliceWidth;
        draft.byId[action.id].roiDz =
          (draft.byId[action.id].dz * action.percentSliceWidth) / 100; // in mm
        break;
      }

      case SET_BMODE_THICKNESS: {
        const newRoiZ = Number(((action.top + action.bottom) / 2).toFixed(5)); // in meters
        draft.byId[action.id].thickness = Math.abs(action.bottom - action.top); // in mm
        draft.byId[action.id].topSurface = action.top; // in mm
        draft.byId[action.id].bottomSurface = action.bottom; // in mm
        draft.byId[action.id].zValue = newRoiZ; // in mm
        break;
      }

      case TOGGLE_BMODE_SET_TO_AUTO_DETECTED_THICKNESS: {
        draft.byId[action.id].setToAutoDetectedThickness = !draft.byId[
          action.id
        ].setToAutoDetectedThickness; // in mm
        break;
      }

      case SET_SAMPLE_LOCATION_ID: {
        draft.byId[action.sampleId].locationId = action.locationId;
        break;
      }

      case SET_SAMPLE_TRIANGLE_POSITION: {
        draft.trianglePositionY = action.position;
        break;
      }

      // REMOVE_ACQ_SAMPLE and REMOVE_ERRORED_SAMPLE are the same for changing samples state, but different for changing locations state
      case REMOVE_ERRORED_SAMPLE: {
        draft.byId = action.samples;
        draft.allIds = action.allIds;
        draft.idByType[action.acqType] = action.idByType;
        break;
      }

      case REMOVE_ACQ_SAMPLE: {
        draft.byId = action.samples;
        draft.allIds = action.allIds;
        draft.idByType[action.acqType] = action.idByType;
        break;
      }

      case RESET_REDUX:
        return initialState;

      default:
        return draft;
    }
  });
